import React, { memo } from 'react';
import { useDropzone } from 'react-dropzone';
import { FormattedMessage, useIntl } from 'react-intl';

import { Input } from '../../types/Input';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import { Layout } from '../Layout/Layout';
import { Text } from '../Text/Text';

interface Props extends Input<File, true> {
  /** Accepted file types (recommend organizing collections via enums, see https://developer.mozilla.org/en-US/docs/Web/API/window/showOpenFilePicker) */
  accept?: Record<string, string[]>;
  /** Single vs multiple files */
  isMulti?: boolean;
  /** Max number of files */
  maxFiles?: number;
  /** Max file size (in bytes) */
  maxSize?: number;
  /** Min file size (in bytes) */
  minSize?: number;
  /** UI mode */
  mode?: 'card';
}

export const FileInput = memo(
  ({
    accept = {},
    autoFocus,
    disabled,
    error,
    isMulti,
    maxFiles,
    maxSize,
    minSize,
    mode,
    name,
    onChange,
    placeholder,
    width,
  }: Props) => {
    const intl = useIntl();
    const handleDrop = <T extends File>(acceptedFiles: T[]) => {
      const nonEmptyFiles = acceptedFiles.filter((file) => file.size > 0);
      return onChange(nonEmptyFiles);
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      accept,
      autoFocus,
      disabled,
      maxFiles,
      maxSize,
      minSize,
      multiple: isMulti,
      onDrop: handleDrop,
    });

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(Array.from(event.target.files ?? []));
    };

    const nativeInput = (
      <input
        {...getInputProps()}
        name={name}
        placeholder={placeholder}
        type="file"
        onChange={handleChange}
      />
    );

    const isCard = mode === 'card';
    const direction = isCard ? 'column' : 'row';
    const iconSize = isCard ? 'l' : 'm';
    const textVariant = isCard ? 'section' : 'body';

    const fileTypeList = Object.values(accept).flat().join(', ');

    const instructions = isDragActive ? (
      <strong>
        <FormattedMessage
          id="FileInput.instructionsText.activeDragAndDrop"
          defaultMessage="Drop files here…"
          description="Instructions for active drag and drop area"
        />
      </strong>
    ) : (
      (placeholder ?? (
        <>
          <strong>
            <FormattedMessage
              id="FileInput.defaultPlaceholderText.dragAndDrop"
              defaultMessage="Drag and drop "
              description="Default bolded placeholder instructions for drag and drop area"
            />{' '}
          </strong>
          <Text color="text.secondary" variant={textVariant}>
            {isMulti ? (
              <FormattedMessage
                id="FileInput.defaultPlaceholderText.multiFileType"
                defaultMessage="{fileTypeList} files"
                description="File type list and pluralized file/files for placeholder. Example: fileTypeList = 'PDF, Word'"
                values={{
                  fileTypeList,
                }}
              />
            ) : (
              <FormattedMessage
                id="FileInput.defaultPlaceholderText.singleFileType"
                defaultMessage="a {fileTypeList} file"
                description="File type list and pluralized file/files for placeholder. Example: fileTypeList = 'PDF'"
                values={{
                  fileTypeList,
                }}
              />
            )}
          </Text>
        </>
      ))
    );

    return (
      <div {...getRootProps()}>
        <Layout
          align="center"
          direction={direction}
          disabled={disabled}
          preset="buttons"
          styles={componentStyles.container}
          styleProps={{ error, width }}
        >
          <Icon icon="upload" size={iconSize} />
          <Text flex="auto" variant={textVariant}>
            {instructions}
          </Text>
          <Text color="text.secondary" textTransform="uppercase">
            <FormattedMessage
              id="FileInput.conjunctionText"
              defaultMessage="or"
              description="Conjunction text for options to upload files"
            />
          </Text>
          <Button
            text={intl.formatMessage({
              id: 'FileInput.uploadButton',
              defaultMessage: 'Upload from computer',
              description: 'Button to upload files',
            })}
          />
          {nativeInput}
        </Layout>
      </div>
    );
  },
);

const componentStyles = {
  container: {
    backgroundColor: 'background.quiet',
    border: 'border',
    borderRadius: 'm',
    borderStyle: 'dashed',
    paddingBottom: 3,
    paddingLeft: 4,
    paddingRight: 4,
    paddingTop: 3,
  },
};
